import { useState } from "react";
import {
  DEFAULT_RANGE,
  dayHoursProblem,
  fromDayHours,
  hoursSummary,
  splitRange,
  toDayHours,
  WEEKDAYS,
  type DayHours,
} from "../../utils/openingHours";
import type { OpeningHoursRange } from "../../api/types";

/**
 * G-203 redesign — the week as seven rows: day, an on/off switch, the times,
 * a split control. The whole week fits one panel at 1440 and one sheet at
 * 375 (the old table needed two scroll axes). "Same as Monday" fills Tue–Fri.
 */
export function HoursEditor({
  stored,
  busy,
  error,
  onSave,
  onCancel,
}: {
  stored: OpeningHoursRange[];
  busy: boolean;
  error: string | null;
  onSave: (next: OpeningHoursRange[]) => void;
  onCancel: () => void;
}) {
  const [days, setDays] = useState<DayHours[]>(() => toDayHours(stored));
  const problem = dayHoursProblem(days);
  const preview = fromDayHours(days);

  const update = (weekday: number, patch: (d: DayHours) => DayHours) =>
    setDays((prev) => prev.map((d) => (d.weekday === weekday ? patch(d) : d)));

  const sameAsMonday = () => {
    const mon = days[0];
    setDays((prev) =>
      prev.map((d) =>
        d.weekday >= 2 && d.weekday <= 5
          ? { ...d, closed: mon.closed, ranges: mon.ranges.map((r) => ({ ...r })) }
          : d,
      ),
    );
  };

  return (
    <form
      className="shop-hours-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (!problem) onSave(preview);
      }}
    >
      <div className="shop-hours-tools">
        <button type="button" className="button plain" onClick={sameAsMonday} disabled={busy}>
          Same as Monday, Tue–Fri
        </button>
      </div>
      {days.map((d) => {
        const name = WEEKDAYS[d.weekday - 1];
        return (
          <div key={d.weekday} className={`shop-hours-day${d.closed ? " is-closed" : ""}`}>
            <span className="shop-hours-dayname">{name.short}</span>
            <button
              type="button"
              role="switch"
              aria-checked={!d.closed}
              aria-label={`${name.long} open`}
              className={`shop-switch${d.closed ? "" : " is-on"}`}
              disabled={busy}
              onClick={() =>
                update(d.weekday, (day) => ({
                  ...day,
                  closed: !day.closed,
                  ranges: !day.closed || day.ranges.length > 0 ? day.ranges : [{ ...DEFAULT_RANGE }],
                }))
              }
            >
              <span className="shop-switch-knob" />
            </button>
            {d.closed ? (
              <span className="shop-hours-closed">Closed</span>
            ) : (
              <div className="shop-hours-ranges">
                {d.ranges.map((r, i) => (
                  <div key={i} className="shop-hours-range">
                    <input
                      type="time"
                      value={r.opensAt}
                      aria-label={i === 0 ? `${name.long} opens` : `${name.long} opens again`}
                      disabled={busy}
                      onChange={(e) =>
                        update(d.weekday, (day) => ({
                          ...day,
                          ranges: day.ranges.map((x, j) => (j === i ? { ...x, opensAt: e.target.value } : x)),
                        }))
                      }
                    />
                    <span className="muted">–</span>
                    <input
                      type="time"
                      value={r.closesAt}
                      aria-label={i === 0 ? `${name.long} closes` : `${name.long} closes again`}
                      disabled={busy}
                      onChange={(e) =>
                        update(d.weekday, (day) => ({
                          ...day,
                          ranges: day.ranges.map((x, j) => (j === i ? { ...x, closesAt: e.target.value } : x)),
                        }))
                      }
                    />
                    {i > 0 ? (
                      <button
                        type="button"
                        className="icon-button shop-hours-icon"
                        aria-label={`Remove ${name.long}'s second range`}
                        disabled={busy}
                        onClick={() =>
                          update(d.weekday, (day) => ({ ...day, ranges: day.ranges.filter((_, j) => j !== i) }))
                        }
                      >
                        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 2l10 10M12 2L2 12" /></svg>
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            {!d.closed && d.ranges.length === 1 && splitRange(d.ranges[0]).length === 2 ? (
              <button
                type="button"
                className="icon-button shop-hours-icon"
                aria-label={`Split ${name.long} into two ranges`}
                disabled={busy}
                onClick={() => update(d.weekday, (day) => ({ ...day, ranges: splitRange(day.ranges[0]) }))}
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 8h4M9 8h4M8 3v10" /></svg>
              </button>
            ) : (
              <span />
            )}
          </div>
        );
      })}
      <p className="muted shop-hours-preview">{hoursSummary(preview)}</p>
      {problem ? <p className="shop-section-error">{problem}</p> : null}
      {error ? <p className="shop-section-error">{error}</p> : null}
      <div className="button-row">
        <button type="submit" className="button" disabled={busy || problem !== null}>
          {busy ? "Saving…" : "Save hours"}
        </button>
        <button type="button" className="button plain" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
